import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/kit/Button';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { Icon } from '@/kit/Icon';
import { Select } from '@/kit/Select';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { selectedClient } from '@/core/selectedClient';
import { groupService } from '@/core/services/group';
import { ExportFormatCode } from '@/core/models/practice.models';
import type { GroupSummaryRow, GroupTrialBalanceRow, GroupTrialBalanceView } from '@/core/models/group.models';
import { formatBracketed, formatNatural } from '@/core/format/naturalBalance';
import { useMounted } from './useMounted';
import './TaxreturnGroupTrialbalance.scss';

/** The amount for one reporting code in one column; blank cells are shown as zero. */
function cell(row: GroupTrialBalanceRow, columnKey: string): number {
  return row.cells.find((c) => c.columnKey === columnKey)?.amount ?? 0;
}

export function TaxreturnGroupTrialbalance() {
  const mounted = useMounted();

  const [groups, setGroups] = useState<GroupSummaryRow[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [view, setView] = useState<GroupTrialBalanceView | null>(null);

  const load = useCallback(
    (groupId: string | null) => {
      if (!groupId) return;
      groupService
        .getTrialBalance(groupId)
        .then((result) => {
          if (mounted.current) setView(result);
        })
        .catch((error) => {
          if (mounted.current) notifyError(error);
        });
    },
    [mounted],
  );

  useEffect(() => {
    const client = selectedClient();
    groupService
      .getGroups(client?.id ?? null)
      .then((result) => {
        if (!mounted.current) return;
        setGroups(result);
        const first = result.length > 0 ? result[0].groupPeriodId : null;
        setSelectedGroupId(first);
        if (first) {
          load(first);
        } else {
          setView(null);
        }
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
    // Runs once, like ngOnInit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Rebuilds the projection from the undertakings and the posted group journals. */
  const rebuild = () => {
    if (!selectedGroupId) {
      notifyError('Select a group first.');
      return;
    }
    groupService
      .rebuildTrialBalance(selectedGroupId)
      .then((result) => {
        if (!mounted.current) return;
        setView(result);
        notifySuccess('The group trial balance has been rebuilt.');
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
  };

  const onExport = (format: ExportFormatCode, isPrint = false) => {
    if (!selectedGroupId) {
      notifyError('Select a group first.');
      return;
    }
    groupService.exportTrialBalance({ groupPeriodId: selectedGroupId, format, isPrint }).catch((error) => {
      if (mounted.current) notifyError(error);
    });
  };

  // Angular only had displayedColumns once a view had been applied.
  const columns: DataTableColumn<GroupTrialBalanceRow>[] = !view
    ? []
    : [
        { id: 'code', header: 'Code', cell: (row) => row.code },
        { id: 'description', header: 'Description', cell: (row) => row.description },
        ...view.columns.map((column): DataTableColumn<GroupTrialBalanceRow> => ({
          id: column.columnKey,
          headerClassName: 'text-right',
          cellClassName: 'text-right',
          header: (
            <>
              {column.columnName}
              {column.ownershipPercent !== null && <span> ({column.ownershipPercent}%)</span>}
            </>
          ),
          // APT349: shown on the reporting code's natural side (positive without a sign, the other side in brackets); the stored amounts are unchanged.
          cell: (row) => formatNatural(cell(row, column.columnKey), row.normalSign),
        })),
        {
          id: 'consolidated',
          header: 'Consolidated',
          headerClassName: 'text-right',
          cellClassName: 'text-right',
          cell: (row) => <strong>{formatNatural(row.consolidatedAmount, row.normalSign)}</strong>,
        },
      ];

  return (
    <SideMenuPage menu="task" contentClassName="taxreturn-group-trialbalance">
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <Select<GroupSummaryRow>
              className="w-300"
              openOnFocus
              panelClass="myPanelClass"
              items={groups}
              bindLabel="groupName"
              bindValue="groupPeriodId"
              clearable={false}
              value={selectedGroupId}
              onChange={(value) => {
                const groupId = value as string | null;
                setSelectedGroupId(groupId);
                load(groupId);
              }}
            />
            <Button className="action-button primary-button ml-1" disabled={!selectedGroupId} onClick={rebuild}>
              <Icon name="sync" outlined />
              <span>Rebuild</span>
            </Button>
          </div>

          <div className="content-header-right mb-1">
            <div className="toolbar">
              <Button title="Export to Excel" onClick={() => onExport(ExportFormatCode.Excel)}>
                <Icon name="border_all" className="icon-color-xls" />
              </Button>
              <Button title="Export to PDF" onClick={() => onExport(ExportFormatCode.Pdf)}>
                <Icon name="picture_as_pdf" outlined className="icon-color-pdf" />
              </Button>
              <Button title="Print" onClick={() => onExport(ExportFormatCode.Pdf, true)}>
                <Icon name="print" outlined />
              </Button>
            </div>
          </div>
        </div>

        {view && (
          <div className="mb-1">
            <p className="mb-0">
              <strong>{view.groupName}</strong> — {view.parentName} — {view.periodLabel}
            </p>
            {view.isStale && (
              <p className="text-danger mb-0">
                {view.staleReason || 'The group trial balance is out of date.'} Rebuild it before relying on these
                figures.
              </p>
            )}
          </div>
        )}

        <div className="mat-table-wrapper">
          <DataTable columns={columns} rows={view ? view.rows : []} rowKey={(row) => row.reportingCodeId} />
        </div>

        {/* #154: a rebuild with nothing to consolidate used to show only zero totals, with no reason. */}
        {view && !view.rows.length && (
          <p className="text-muted mt-1 mb-0">
            There are no balances to consolidate yet. Each included undertaking needs a balanced annual trial balance
            posted to the accounting period selected for it on Group Structure, with its nominal codes mapped (Nominal
            Mapping). Group Structure says which step each undertaking is missing. Then rebuild here.
          </p>
        )}

        {view && (
          <div className="mt-1">
            <p className="mb-0">
              Undertakings and parent: <strong>{formatBracketed(view.totals.undertakingsTotal)}</strong> &nbsp;|&nbsp; Group
              adjustments and eliminations: <strong>{formatBracketed(view.totals.groupAdjustmentsTotal)}</strong>{' '}
              &nbsp;|&nbsp; Consolidated: <strong>{formatBracketed(view.totals.consolidatedTotal)}</strong>
            </p>
            <p className={`${view.totals.reconciles ? 'text-success' : 'text-danger'} mb-0`}>
              {view.totals.reconciles
                ? 'The consolidated total reconciles to the undertakings plus the group journals.'
                : 'The consolidated total does not reconcile. Rebuild the group trial balance.'}
            </p>
          </div>
        )}
      </div>
    </SideMenuPage>
  );
}
